import { useCallback } from "react";
import useAuth from "hooks/useAuth";
import { sleep } from "utils/Haim/utils";
import {
  getListingPresentationForAdd,
  createFavoriteIfNotExistsAndAddListingWithEdits
} from "utils/Haim/firestoreUtils";

type AddListingToBuyersArgs = {
  listingId: string;
  /** Contact phone numbers (already filtered by the confidence threshold). */
  contactPhones: string[];
  /** Called per contact after the listing is added to their minisite. */
  onContactAdded?: (phoneNumber: string) => void;
  setLoading?: (loading: boolean) => void;
};

/**
 * Adds a listing to one or more buyers' minisites, carrying the realtor's presentation of that
 * listing (photos + description) onto each new one.
 *
 * The presentation comes from the realtor's GLOBAL record (`listing-presentations`), which is the
 * authority since 2026-09-30 — not from scanning their other minisites, which was a simulation of
 * exactly that state and could not see a listing edited before it was ever shared. Editing the
 * listing later re-applies to everyone through the server's fan-out, so this only has to seed the
 * minisites that did not exist yet. It is looked up ONCE for all the buyers, through the lookup every
 * add in the dashboard shares (`getListingPresentationForAdd`), which falls back to the old scan when
 * the server cannot be reached.
 */
export function useAddListingWithEditsToBuyers(): (args: AddListingToBuyersArgs) => Promise<void> {
  const {
    user: { email }
  } = useAuth();

  return useCallback(
    async ({ listingId, contactPhones, onContactAdded, setLoading }: AddListingToBuyersArgs): Promise<void> => {
      // Mirror the existing UX: a click with no matching contacts shows a brief
      // spinner rather than silently doing nothing.
      if (!contactPhones.length) {
        setLoading?.(true);
        await sleep(1000);
        setLoading?.(false);
        return;
      }

      setLoading?.(true);
      try {
        const edits = await getListingPresentationForAdd(email, listingId);

        await Promise.all(
          contactPhones.map(async (phoneNumber) => {
            if (!phoneNumber) return;
            await createFavoriteIfNotExistsAndAddListingWithEdits(email, phoneNumber, listingId, edits);
            onContactAdded?.(phoneNumber);
          })
        );
      } catch (error) {
        console.error("Error adding listing to buyers", error);
      } finally {
        setLoading?.(false);
      }
    },
    [email]
  );
}
